import { Avatar, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import type { SiteSelect } from '@ui/Types/AuthServer';

import { dayjs } from 'kerberus-components/src/Components/Dayjs/dayjs-utils';

import ChartWidget from './ChartWidget';

interface TimezoneClockWidgetProps {
    tz?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function TimezoneClockWidget({ tz }: Readonly<TimezoneClockWidgetProps>): React.JSX.Element {
    const [currentDate, setCurrentDate] = useState<string>('');
    const [currentDay, setCurrentDay] = useState<string>('');
    const [currentTime, setCurrentTime] = useState<string>('');
    const [timezone, setTimezone] = useState<string>('');

    useEffect(() => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezone(userTimezone);

        const intervalId = setInterval(() => {
            const now = dayjs().tz(userTimezone);
            setCurrentDay(now.format('dddd')); // Day (e.g., Monday)
            setCurrentDate(now.format('MMMM DD YYYY')); // Date (e.g., September 25th 2024)
            setCurrentTime(now.format('HH:mm:ss')); // 24-hour time (e.g., 14:30:15)
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <Box
            sx={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                color: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                p: 2,
                textAlign: 'center',
                width: '100%',
            }}
        >
            <Typography align="center" sx={{ fontSize: '3cqmin !important' }}>
                {currentDay}
            </Typography>
            <Typography align="center" sx={{ fontSize: '2cqmin !important' }}>
                {timezone}
            </Typography>
            <Typography align="center" sx={{ fontSize: '2cqmin !important' }}>
                {currentDate}
            </Typography>
            <Typography align="center" sx={{ fontSize: '4cqmin !important' }}>
                {currentTime}
            </Typography>
        </Box>
    );
}

function ImageUploader() {
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageSrc(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <Box
            sx={{
                alignItems: 'center',
                backgroundImage: imageSrc ? `url(${imageSrc})` : 'none',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                transition: 'background-image 0.3s ease-in-out',
                width: '100%',
            }}
        >
            {!imageSrc && (
                <Button component="label" variant="contained">
                    Upload Image
                    <input accept="image/*" hidden onChange={handleImageChange} type="file" />
                </Button>
            )}
            {imageSrc ? (
                <Button onClick={() => setImageSrc(null)} sx={{ display: 'none' }} variant="contained">
                    Remove Image
                </Button>
            ) : null}
        </Box>
    );
}

interface WidgetRendererProps {
    selectedWidget: string | null;
    sites: SiteSelect[];
}

export default function WidgetRenderer({ selectedWidget, sites }: Readonly<WidgetRendererProps>) {
    switch (selectedWidget) {
        case 'clock':
            return <TimezoneClockWidget />;
        case 'image':
            return null;
        case 'chart':
            return <ChartWidget sites={sites} />;
        case 'people':
            return (
                <Stack
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Card
                        sx={{
                            backgroundColor: 'lightgreen',
                            height: '25cqh',
                            maxWidth: 500,
                            width: '10cqw',
                        }}
                    >
                        <CardContent
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                justifyContent: 'center',
                            }}
                        >
                            <Stack
                                spacing={0.25}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Avatar
                                    alt="Profile 1"
                                    src="https://i.pravatar.cc/150?img=1"
                                    sx={{
                                        height: 'auto',
                                        margin: '0 auto',
                                        maxWidth: '256px',
                                        width: '8cqmin',
                                    }}
                                />
                                <Typography align="center" sx={{ fontSize: '1cqw !important' }}>
                                    Example Card 1
                                </Typography>
                                <Stack spacing={-0.5}>
                                    <Typography align="center" sx={{ fontSize: '1.25cqmin !important' }}>
                                        FirstName
                                    </Typography>
                                    <Typography align="center" sx={{ fontSize: '1.25cqmin !important' }}>
                                        LastName
                                    </Typography>
                                </Stack>
                                <Typography align="center" sx={{ fontSize: '1cqmin !important' }}>
                                    10 minutes ago
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            ); // Replace with your People component
        case 'any':
            return (
                <Stack sx={{ alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center' }}>
                    Any Component
                </Stack>
            ); // Replace with your Any component
        default:
            return null; // No selection
    }
}
